import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import {
  defaultTargetingBuilderFilter,
  TargetingStatus,
  type TargetingBuilderFilter,
} from './model';

export type TargetingBuilderRouteSearch = {
  search?: string;
  statuses?: string;
  page?: number;
  size?: number;
};

export type TargetingBuilderRouteFilters = Pick<
  TargetingBuilderFilter,
  'searchTerm' | 'statuses' | 'page' | 'size'
>;

const targetingStatuses = new Set<string>(Object.values(TargetingStatus));

export function targetingBuilderRouteSearchSchema(
  search: Record<string, unknown>,
): TargetingBuilderRouteSearch {
  const searchTerm = optionalTrimmedString(search['search']);
  const statuses = typeof search['statuses'] === 'string' ? search['statuses'].trim() : undefined;
  const page = optionalPositiveInteger(search['page']);
  const size = optionalPositiveInteger(search['size']);
  return {
    ...(searchTerm ? { search: searchTerm } : {}),
    ...(statuses !== undefined ? { statuses } : {}),
    ...(page ? { page } : {}),
    ...(size ? { size } : {}),
  };
}

export function parseTargetingBuilderRouteSearch(
  search: TargetingBuilderRouteSearch,
): TargetingBuilderRouteFilters {
  return {
    searchTerm: search.search ?? defaultTargetingBuilderFilter.searchTerm,
    statuses: parseStatuses(search.statuses),
    page: search.page ? search.page - 1 : defaultTargetingBuilderFilter.page,
    size: search.size ?? defaultTargetingBuilderFilter.size,
  };
}

export function serializeTargetingBuilderRouteSearch(
  filters: TargetingBuilderRouteFilters,
): TargetingBuilderRouteSearch {
  const statuses = filters.statuses.join(',');
  const defaultStatuses = defaultTargetingBuilderFilter.statuses.join(',');
  return {
    ...(filters.searchTerm.trim() ? { search: filters.searchTerm.trim() } : {}),
    ...(statuses !== defaultStatuses ? { statuses } : {}),
    ...(filters.page ? { page: filters.page + 1 } : {}),
    ...(filters.size !== defaultTargetingBuilderFilter.size ? { size: filters.size } : {}),
  };
}

function parseStatuses(value: string | undefined): TargetingStatus[] {
  if (value === '') return [];
  if (!value) return [...defaultTargetingBuilderFilter.statuses];
  return value
    .split(',')
    .filter((status): status is TargetingStatus => targetingStatuses.has(status));
}
